import React, { useEffect, useState } from 'react';
import { Business, AppointmentData, Service } from '../types';
import Utils from '@/utils/util';

import { BackNav } from '@/components/BackNavigation/BackNavigation';
import UserIcon from '@/assets/img/user-icon.png';
import CatDefaultIcon from '@/assets/img/v2/pet-cat-default-icon.svg';
import DogDefaultIcon from '@/assets/img/v2/pet-dog-default-icon.svg';

import './NonRegistrationConfirmation.scss';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PetConfirmationDisplay } from './PetConfirmationDisplay';
import { useTranslation } from 'react-i18next';
import { businessCurrency } from '@/features/Language/api/getCountryCurrency';

type BookingConfirmationProps = {
  appointmentData?: AppointmentData;
  business?: Business;
  onBack: (page: 'payment-method'| 'pet-profile') => void;
  onComplete: (data: AppointmentData, isNonRegistration?: boolean) => void;
  isLoading: boolean;
  isPaymentViaLink?: boolean;
};
export const NonRegistrationConfirmation = ({
  appointmentData,
  business,
  onBack,
  onComplete,
  isLoading,
  isPaymentViaLink,
}: BookingConfirmationProps) => {
  const [showServiceDetails, setShowServiceDetails] = useState<boolean>(false);
  const { t } = useTranslation();
  useEffect(() => {
    Utils.scrollToTop();
  }, []);

  const onBackArrowClick = () => {
    if (appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING) {
      onBack &&
        onBack(
          appointmentData?.service?.price && appointmentData?.service?.price > 0
            ? 'payment-method'
            : 'pet-profile'
        );
    } else {
      onBack &&
        onBack(
          appointmentData?.cost?.fee && appointmentData?.cost?.fee > 0
            ? 'payment-method'
            : 'pet-profile'
        );
    }
  };
  const last4Digit = appointmentData?.paymentCard?.number;
  const onAppointmentConfirm = () => {
    appointmentData && onComplete(appointmentData, true);
  };
  const getAdvancePayment = () => {
    let amountToPay = 0;
    if (business) {
      const advBookingPercentage = appointmentData?.selectedLocation?.advBookingPercentage  ? 
        parseFloat(appointmentData?.selectedLocation?.advBookingPercentage) : 0;
      const advBookingAmount = appointmentData?.selectedLocation?.advBookingAmount ? 
        parseFloat(appointmentData?.selectedLocation?.advBookingAmount) : 0;
      let totalPayment = parseFloat(Utils.getPriceFromService(appointmentData?.service)+'');

      const addonsPrice = appointmentData?.selectedAddons
        ? Utils.getAddonsPrice(appointmentData?.selectedAddons)
        : 0;
      totalPayment = parseFloat(totalPayment.toString()) + addonsPrice;
      if(advBookingPercentage > 0) {
        amountToPay = calculatePercentage(Number(advBookingPercentage), Number(totalPayment));
      } else if(advBookingAmount) {
        amountToPay = advBookingAmount
      }
    }
    return Utils.roundDecimal(
      Number(appointmentData?.amount ? appointmentData.amount : amountToPay)
    );
  };
  const calculatePercentage = (advPayment: number, totalPayment: number) => {
    if (isNaN(advPayment) || isNaN(totalPayment) || totalPayment === 0) {
      return 0;
    }
    const percentage = (advPayment / 100) * totalPayment;
    return percentage;
  };
  let servicePrice = 0;
  if (appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING) {

    servicePrice = appointmentData?.amount ? parseFloat(appointmentData?.amount) : Utils.getPriceFromService(
      appointmentData.service
    );
  } else {
    servicePrice =
      appointmentData?.cost && appointmentData?.cost?.fee
        ? appointmentData?.cost?.fee
        : appointmentData?.amount
        ? parseFloat(appointmentData?.amount)
        : 0;
  }
  const petData = appointmentData?.petProfile
    ? [appointmentData.petProfile]
    : appointmentData?.petProfiles;
  return (
    <div className="non-reg-booking-confirmation">
      <div className="profile-container">
        <BackNav onClick={onBackArrowClick} />
        <h5>{t('Booking')} {isPaymentViaLink ? t('Details') : t('Confirmation')}</h5>
        <div className="scroller">
          <p className="desc">{`Verify your ${
            appointmentData?.service ? 'service' : 'booking'
          } details below`}</p>
          {(appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING ||
            appointmentData?.service) && (
            <>
              <div className="service-details">
                <div className="header">
                  <label>
                    {appointmentData?.room
                      ? appointmentData.room.name
                      : appointmentData?.service?.name}
                    {appointmentData?.selectedAddons && appointmentData?.selectedAddons.length > 0 && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-id" className="addons-tooltip-content">
                            <div className="addons-tooltips">
                              <p>{t('Add-ons')}</p>
                              {appointmentData?.selectedAddons.map((addons: Service) => (
                                <ul>
                                  <li>{addons.name}</li>
                                </ul>
                              ))}
                            </div>
                          </Tooltip>
                        }
                      >
                        <span className="addons-service-count" style={{ cursor: 'pointer' }}>
                          +{appointmentData?.selectedAddons.length}
                        </span>
                      </OverlayTrigger>
                    )}
                  </label>
                  <label
                    className="pink"
                    onClick={() => setShowServiceDetails(!showServiceDetails)}
                  >
                    {showServiceDetails ? t('- Less Details') : t('+ More Details')}
                  </label>
                </div>
                <p className={`service-desc ${showServiceDetails ? '' : 'clip'}`}>
                  {appointmentData?.service?.description}
                </p>
                <div className={`details ${showServiceDetails ? '' : 'hide'}`}>
                  <div className="detail">
                    <label>Service Provider</label>
                    <div className="host-details">
                      <img
                        src={
                          appointmentData?.provider?.avatarUrl
                            ? appointmentData.provider.avatarUrl
                            : UserIcon
                        }
                      />
                      <div>
                        <p>{appointmentData?.provider?.name}</p>
                        <p>{appointmentData?.provider?.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="detail">
                    <label>Duration</label>
                    <p>
                      {appointmentData?.duration && Utils.minutesToDhm(appointmentData?.duration)}
                    </p>
                  </div>
                  {appointmentData?.selectedAddons && appointmentData?.selectedAddons.length > 0 && (
                    <div className="detail addons">
                      <label>Add-on services</label>
                      <div className="duration-date">
                        <ul className="addon-services">
                          {appointmentData?.selectedAddons.map((addonService) => (
                            <li>{addonService.name}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          <div className="details additional">
            <div className="">
              {!appointmentData?.room &&
                appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING && (
                  <div className="detail">
                    <label>{t('Service Detail')}</label>
                    <p>
                      <span className="seperator">
                        {Utils.getOnlyDate(
                          appointmentData?.slot?.start,
                          appointmentData?.selectedLocation?.timezone,
                          'MMMM DD, YYYY'
                        )}{' '}
                      </span>
                      <span>
                        {' '}
                        {appointmentData?.slot &&
                          Utils.getOnlyDate(
                            appointmentData?.slot.start,
                            appointmentData?.selectedLocation?.timezone,
                            'hh:mm A'
                          )}{' '}
                        -{' '}
                        {appointmentData?.slot &&
                          Utils.getOnlyDate(
                            appointmentData?.slot.end,
                            appointmentData?.selectedLocation?.timezone,
                            'hh:mm A'
                          )}{' '}
                        <span className="text-gray-500 text-sm">
                          {Utils.getTzAbbr(appointmentData?.selectedLocation?.timezone)}
                        </span>
                      </span>
                    </p>
                  </div>
                )}
              {(appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.BOARDING ||
                appointmentData?.accomodationType === Utils.ACCOMODATION_TYPES.BOARDING) && (
                <>
                  <div className="detail">
                    <label>{t('Service Detail')}</label>
                    <p>
                      <span className="seperator">Boarding Service</span>
                      {appointmentData?.cost?.days && <span>{appointmentData?.cost?.days} {appointmentData?.cost?.days > 1 ? "days" : "day"}</span>}
                      {isPaymentViaLink &&<span>{Utils.totalDaysBooked(appointmentData, undefined, business?.timezone)}</span>}
                    </p>
                  </div>
                  <div className="detail">
                    <label>{t('Check-in Time')}</label>
                    <p>
                      {appointmentData?.startDate
                        ? Utils.formatDateStringTz(
                            appointmentData?.startDate,
                            isPaymentViaLink ? appointmentData?.selectedLocation?.timezone : undefined,
                            "DD MMM 'yy "
                          )
                        : Utils.formatDateStringTz(
                            appointmentData?.slot?.start,
                            appointmentData?.selectedLocation?.timezone,
                            "DD MMM 'yy "
                          )}
                      {Utils.getTimeLabel(appointmentData?.selectedLocation?.boardingCheckIn)
                        .replace(/\s+/g, '')
                        .toLowerCase()}
                    </p>
                  </div>
                  <div className="detail">
                    <label>{t('Check-out Time')}</label>
                    <p>
                      {appointmentData?.endDate ?
                        Utils.formatDateStringTz(
                          appointmentData?.endDate,
                          isPaymentViaLink ? appointmentData?.selectedLocation?.timezone : undefined,
                          "DD MMM 'yy "
                        ) : Utils.formatDateStringTz(
                          appointmentData?.slot?.end,
                          appointmentData?.selectedLocation?.timezone,
                          "DD MMM 'yy "
                        )}
                      {Utils.getTimeLabel(appointmentData?.selectedLocation?.boardingCheckOut)
                        .replace(/\s+/g, '')
                        .toLowerCase()}
                    </p>
                  </div>
                </>
              )}
              {(appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.DAY_CARE ||
                appointmentData?.accomodationType === Utils.ACCOMODATION_TYPES.DAY_CARE) && (
                <>
                  <div className="detail">
                    <label>{t('Service Detail')}</label>
                    <p>
                      <span className="seperator">{t('Day-Care Service')}</span>
                      <span>
                        {' '}
                        {Utils.minutesToDhm(
                          appointmentData?.duration ? appointmentData?.duration : 0
                        )}
                      </span>
                    </p>
                  </div>
                  <div className="detail">
                    <label>{t('Check-in Time')}</label>
                    <p>
                      {appointmentData?.slot &&
                        Utils.formatDateStringTz(
                          appointmentData?.slot?.start,
                          appointmentData?.selectedLocation?.timezone,
                          "DD MMM 'yy, hh:mma "
                        )}
                    </p>
                  </div>
                  <div className="detail">
                    <label>{t('Check-out Time')}</label>
                    <p>
                      {appointmentData?.slot &&
                        Utils.formatDateStringTz(
                          appointmentData?.slot?.end,
                          appointmentData?.selectedLocation?.timezone,
                          "DD MMM 'yy, hh:mma "
                        )}
                    </p>
                  </div>
                </>
              )}
              {petData && petData.length > 0 && (
                <div className="detail pet-list">
                  <label>Pet Detail</label>
                  <div className="pet-list-display">
                    {petData.map((pet, index) => (
                      <PetConfirmationDisplay data={pet} showPrice={false} />
                    ))}
                  </div>
                </div>
              )}
              <div className="detail">
                <label>{t('Pet Parent Detail')}</label>
                {appointmentData?.userData && (
                  <p>
                    <span className="seperator">{appointmentData.userData.name} </span>
                    <span>
                      {' '}
                      {appointmentData?.userData?.phone &&
                        Utils.formatPhoneNumber(appointmentData?.userData?.phone)}
                    </span>
                  </p>
                )}
              </div>
              {servicePrice > 0 && (
                <>
                  <div className="detail">
                    <label>{t('Credit Card')}</label>
                    {appointmentData?.paymentCard && (
                      <p className="credit-card-no">
                        xxxx xxxx xxxx <span>{last4Digit}</span>
                      </p>
                    )}
                  </div>
                  <div className="detail billing">
                    <label>{t('Billing Address')}</label>
                    {appointmentData?.paymentCard && appointmentData?.paymentCard?.firstName ? (
                      <p>
                        <span className="seperator">
                          {appointmentData.paymentCard.firstName}{' '}
                          {appointmentData.paymentCard.lastName}{' '}
                        </span>
                        <span> {appointmentData.userData?.email}</span>
                      </p>
                    ) : appointmentData?.userData && appointmentData?.userData.name ? (
                      <p>
                        <span className="seperator">{appointmentData.userData.name} </span>
                        <span> {appointmentData.userData?.email}</span>
                      </p>
                    ) : (
                      appointmentData && (
                        <p>
                          <span className="seperator"></span>
                          <span> {appointmentData.userData?.email}</span>
                        </p>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="action">
          <button
            className={`petsked-button button-dark ${servicePrice > 0 ? '' : 'center'} ${
              isLoading ? 'loading' : ''
            }`}
            onClick={onAppointmentConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>{t('Confirming...')}</>
            ) : servicePrice > 0 ? (
              <>
                <span>{t('Complete Booking')}</span>

                {(appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING ||
                  !appointmentData?.selectedServiceType) && (
                  <span>
                    {businessCurrency(business,'USD')}{' '}
                    {getAdvancePayment()}
                  </span>
                )}
                {(appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.DAY_CARE ||
                  appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.BOARDING) && (
                  <span>
                    {businessCurrency(business,'USD')}{' '}
                    {appointmentData.cost &&
                      appointmentData.cost?.advance ? Utils.roundDecimal(appointmentData.cost?.advance) : Utils.roundDecimal(appointmentData.cost?.fee)}
                  </span>
                )}
              </>
            ) : (
              <span>{t('Complete Booking')}</span>
            )}
          </button>
          {appointmentData?.business?.termsOfService &&
            appointmentData?.business?.termsOfService.length > 0 && (
              <p className="terms-and-condition">
                {t('By clicking')} {'"Complete Booking"'} {t('you agree to the')}{' '}
                <a
                  rel="noreferrer"
                  href={window.location.origin + '/' + appointmentData?.business?.slug + '/terms'}
                  target="_blank"
                >
                  {t('Terms of Use')}
                </a>{' '}
                {t('of the provider.')}
              </p>
            )}
        </div>
      </div>
    </div>
  );
};
